import React from "react";
import { Link } from "react-router-dom";

const Terms = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Background Pattern */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_dealauth-api/artifacts/z6ik3546_Subtle%20Pattern%20On%20Blue.png)',
        }}
      />
      
      {/* Side Fade Overlays */}
      <div className="fixed inset-0 bg-gradient-to-r from-slate-900/60 via-transparent to-slate-900/60 pointer-events-none" />
      
      {/* Header */}
      <header className="relative z-10 border-b border-white/10 bg-slate-900/30 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-sm text-cyan-400 hover:text-cyan-300">
              ← Back to Deal Room
            </Link>
            <div className="text-sm font-semibold">Jigsaw Conferences Ltd</div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 md:p-12">
          <h1 className="text-3xl font-bold mb-6">Jigsaw Deal Room – Terms of Use</h1>
          
          <div className="space-y-6 text-slate-300">
            <section>
              <h2 className="text-xl font-semibold text-white mb-3">1. About this Deal Room</h2>
              <p>
                These Terms of Use govern your access to and use of the Jigsaw Deal Room at <strong>dealroom.jigsawconferences.co.uk</strong> (the <strong>Deal Room</strong>). 
                The Deal Room is a private, invitation-only portal used by Jigsaw Conferences Ltd to evaluate potential transactions and investment proposals.
              </p>
              <p className="mt-3">
                By accessing or using the Deal Room, you agree to be bound by these Terms of Use.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">2. Who we are</h2>
              <p>
                The Deal Room is operated by <strong>Jigsaw Conferences Ltd</strong> (<strong>Jigsaw</strong>, <strong>we</strong>, <strong>us</strong>, <strong>our</strong>), 
                a company registered in England and Wales with company number <strong>04788489</strong> and registered office at <strong>3rd Floor, 45 Albemarle Street, London, W1S 4JL, England</strong>.
              </p>
              <p className="mt-3">
                If you have questions about these terms, please contact us at{" "}
                <a href="mailto:enquiries@jigsawconferences.co.uk" className="text-cyan-400 hover:text-cyan-300 underline">
                  enquiries@jigsawconferences.co.uk
                </a>.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">3. Eligibility and permitted users</h2>
              <p>The Deal Room is intended only for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>The specific individuals and organisations who have been invited by Jigsaw; and</li>
                <li>Their authorised representatives who are directly involved in assessing a potential transaction with Jigsaw.</li>
              </ul>
              <p className="mt-3">
                You must not share your login credentials with anyone else or allow unauthorised persons to access the Deal Room.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">4. Your responsibilities</h2>
              <p><strong>You agree to:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Provide information that is accurate, complete and not misleading.</li>
                <li>Use the Deal Room only for the purpose of assessing a potential transaction with Jigsaw.</li>
                <li>Keep your username and password secure and notify us promptly if you suspect unauthorised access.</li>
                <li>Comply with all applicable laws and regulations.</li>
              </ul>
              <p className="mt-3"><strong>You must not:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Attempt to gain unauthorised access to the Deal Room or underlying systems.</li>
                <li>Introduce viruses, malware or other harmful code.</li>
                <li>Copy, scrape, download or distribute content from the Deal Room except as expressly permitted by us in writing.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">5. Confidentiality and use of information</h2>
              <p>
                All information made available to you in the Deal Room is confidential and may include commercially sensitive information, 
                trade secrets and personal data.
              </p>
              <p className="mt-3"><strong>You agree to:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Keep all Deal Room information strictly confidential;</li>
                <li>Use it only for the purpose of assessing a potential transaction with Jigsaw; and</li>
                <li>Not disclose it to any third party except:
                  <ul className="list-disc list-inside ml-6 mt-1">
                    <li>Your professional advisers (who are bound by confidentiality), or</li>
                    <li>As required by law or a competent authority.</li>
                  </ul>
                </li>
              </ul>
              <p className="mt-3">
                Nothing in these Terms restricts your ability to use information that is already lawfully in the public domain.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">6. No offer, no reliance</h2>
              <p>The information in the Deal Room:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Is provided for information purposes only;</li>
                <li>Does not constitute an offer, invitation or commitment by Jigsaw to enter into any transaction; and</li>
                <li>May be updated, supplemented or withdrawn at any time without notice.</li>
              </ul>
              <p className="mt-3">
                You are responsible for carrying out your own due diligence and obtaining independent legal, tax, financial and other 
                advice before entering into any transaction.
              </p>
              <p className="mt-3">
                We do not guarantee that any transaction will be agreed or completed, and we reserve the right to cease discussions at any time.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">7. Intellectual property</h2>
              <p>
                All content in the Deal Room (including text, graphics, logos, documents and code) is owned by Jigsaw or its licensors 
                and is protected by copyright and other intellectual property rights.
              </p>
              <p className="mt-3">
                You are granted a limited, revocable, non-transferable licence to access the Deal Room and view its content solely for 
                the purpose of evaluating a potential transaction with Jigsaw.
              </p>
              <p className="mt-3">
                You must not reproduce, distribute, modify, publicly display or create derivative works from any Deal Room content 
                without our prior written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">8. Data protection</h2>
              <p>
                We process personal data in connection with the Deal Room in accordance with our Privacy Notice, available at{" "}
                <Link to="/legal/privacy" className="text-cyan-400 hover:text-cyan-300 underline">/legal/privacy</Link>.
              </p>
              <p className="mt-3">
                By using the Deal Room, you acknowledge that we will process your personal data, and the personal data of any referees 
                or other individuals you provide, as described in the Privacy Notice.
              </p>
              <p className="mt-3">
                You confirm that any third-party information you provide (for example, referees) has been shared with their knowledge 
                and consent for the purposes described.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">9. Security</h2>
              <p>
                We use reasonable technical and organisational measures to protect the Deal Room and the information it contains. 
                However, we cannot guarantee that the Deal Room will be free from security vulnerabilities or that transmission of 
                information over the internet is completely secure.
              </p>
              <p className="mt-3">
                You are responsible for ensuring that your own systems, devices and connections are secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">10. Limitation of liability</h2>
              <p><strong>Nothing in these Terms limits or excludes our liability for:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>Death or personal injury caused by our negligence;</li>
                <li>Fraud or fraudulent misrepresentation; or</li>
                <li>Any other liability that cannot be limited or excluded under applicable law.</li>
              </ul>
              <p className="mt-3"><strong>Subject to the above:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>We are not responsible for any loss, damage or cost arising from your use of, or reliance on, information in the Deal Room;</li>
                <li>We will not be liable for any indirect, consequential, special or punitive losses; and</li>
                <li>Our aggregate liability to you in connection with the Deal Room will not exceed <strong>£10,000</strong>.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">11. Suspension and termination</h2>
              <p>We may suspend or terminate your access to the Deal Room at any time, with or without notice, if:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mt-2">
                <li>We believe you have breached these Terms;</li>
                <li>Your access is no longer required for the transaction; or</li>
                <li>We decide to discontinue the Deal Room.</li>
              </ul>
              <p className="mt-3">
                On termination, you must promptly stop using the Deal Room and delete any confidential information derived from it, 
                except where you are required to retain it by law or by your professional obligations.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">12. Changes to these Terms</h2>
              <p>
                We may update these Terms from time to time. Where appropriate, we will notify you of material changes (for example, 
                via the Deal Room login page). Your continued use of the Deal Room after changes are made constitutes your acceptance 
                of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">13. Governing law and jurisdiction</h2>
              <p>
                These Terms and any non-contractual obligations arising out of or in connection with them are governed by the laws of 
                <strong> England and Wales</strong>.
              </p>
              <p className="mt-3">
                The courts of England and Wales will have exclusive jurisdiction to settle any disputes arising out of or in connection 
                with these Terms or the Deal Room.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-3">14. Contact</h2>
              <p>For any questions about these Terms or the Deal Room, please contact:</p>
              <p className="mt-2">
                <strong>Email:</strong>{" "}
                <a href="mailto:enquiries@jigsawconferences.co.uk" className="text-cyan-400 hover:text-cyan-300 underline">
                  enquiries@jigsawconferences.co.uk
                </a>
              </p>
              <p className="mt-1">
                <strong>Postal:</strong> Jigsaw Conferences Ltd, 3rd Floor, 45 Albemarle Street, London, W1S 4JL, England
              </p>
            </section>

            <section className="text-sm text-slate-400 mt-8 pt-6 border-t border-white/10">
              <p><strong>Last updated:</strong> January 2025</p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 py-6 text-center text-xs text-slate-400">
        <p>© Jigsaw Conferences Ltd. All rights reserved.</p>
        <div className="mt-2 space-x-2">
          <Link to="/legal/terms" className="hover:text-white">Terms</Link>
          <span>|</span>
          <Link to="/legal/privacy" className="hover:text-white">Privacy</Link>
          <span>|</span>
          <Link to="/legal/cookies" className="hover:text-white">Cookies</Link>
        </div>
      </footer>
    </div>
  );
};

export default Terms;
